import { Plus, ChevronUp, AlertTriangle, Lock } from 'lucide-react';
import { colors } from '../styles/colors';
import { useTempsForm } from '../hooks/useTempsForm';
import TempsFormFields from './temps/TempsFormFields';

/**
 * TempsForm - Formulaire expandable de saisie des temps
 *
 * Refactorisé pour utiliser:
 * - useTempsForm: hook de gestion de l'état et des handlers
 * - TempsFormFields: composant des champs du formulaire
 */
function TempsForm({ onSuccess }) {
  const {
    formData,
    consultants,
    prestataires,
    clientsFiltres,
    projetsFiltres,
    isExpanded,
    setIsExpanded,
    loading,
    showDuplicateModal,
    setShowDuplicateModal,
    duplicateInfo,
    factureBloquee,
    checkingFacture,
    annees,
    canSubmit,
    user,
    isSuperAdmin,
    isVisiteur,
    handleChange,
    handleSubmit
  } = useTempsForm(onSuccess);

  return (
    <>
      {/* Formulaire expandable - visible pour tous mais grisé pour visiteur */}
      <div style={{
        backgroundColor: colors.text.white,
        borderRadius: isExpanded ? '1rem' : '0.5rem',
        boxShadow: isExpanded ? '0 4px 16px rgba(0, 116, 129, 0.15)' : 'none',
        border: isExpanded ? `1px solid ${colors.secondary.lightTeal}` : 'none',
        overflow: 'hidden',
        opacity: isVisiteur() ? 0.6 : 1,
        pointerEvents: isVisiteur() ? 'none' : 'auto',
        transition: 'all 600ms cubic-bezier(0.4, 0, 0.2, 1)',
        width: isExpanded ? '100%' : 'auto',
        display: 'inline-block',
        transform: isExpanded ? 'scale(1)' : 'scale(0.98)',
        transformOrigin: 'left center'
      }}>
        {/* Header cliquable */}
        <ExpandButton
          isExpanded={isExpanded}
          onClick={() => setIsExpanded(!isExpanded)}
        />

        {/* Contenu du formulaire */}
        {isExpanded && (
          <FormContent
            formData={formData}
            consultants={consultants}
            prestataires={prestataires}
            clientsFiltres={clientsFiltres}
            projetsFiltres={projetsFiltres}
            annees={annees}
            factureBloquee={factureBloquee}
            checkingFacture={checkingFacture}
            loading={loading}
            canSubmit={canSubmit}
            handleChange={handleChange}
            handleSubmit={handleSubmit}
            isSuperAdmin={isSuperAdmin}
            user={user}
          />
        )}
      </div>

      {/* Modal de doublon */}
      {showDuplicateModal && (
        <DuplicateModal
          duplicateInfo={duplicateInfo}
          onClose={() => setShowDuplicateModal(false)}
        />
      )}
    </>
  );
}

// === SOUS-COMPOSANTS ===

function ExpandButton({ isExpanded, onClick }) {
  return (
    <button
      onClick={onClick}
      style={{
        width: isExpanded ? '100%' : 'auto',
        padding: isExpanded ? '1.25rem 1.5rem' : '0.625rem 1rem',
        backgroundColor: isExpanded ? colors.secondary.lightTeal : colors.primary.teal,
        border: 'none',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        justifyContent: isExpanded ? 'space-between' : 'center',
        gap: '0.5rem',
        transition: 'all 600ms cubic-bezier(0.4, 0, 0.2, 1)',
        borderBottom: isExpanded ? `1px solid ${colors.secondary.mediumGray}` : 'none',
        borderRadius: isExpanded ? '0' : '0.5rem',
        fontSize: isExpanded ? '1rem' : '0.875rem',
        fontWeight: '600',
        color: colors.text.white,
        boxShadow: isExpanded ? 'none' : '0 2px 4px rgba(0, 116, 129, 0.2)'
      }}
      onMouseEnter={(e) => {
        if (!isExpanded) {
          e.target.style.backgroundColor = '#005a64';
          e.target.style.transform = 'translateY(-2px)';
          e.target.style.boxShadow = '0 4px 8px rgba(0, 116, 129, 0.3)';
        }
      }}
      onMouseLeave={(e) => {
        if (!isExpanded) {
          e.target.style.backgroundColor = colors.primary.teal;
          e.target.style.transform = 'translateY(0)';
          e.target.style.boxShadow = '0 2px 4px rgba(0, 116, 129, 0.2)';
        }
      }}
    >
      {!isExpanded ? (
        <>
          <Plus size={16} color={colors.text.white} />
          Saisir un nouveau temps
        </>
      ) : (
        <>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            <Plus size={24} color={colors.text.white} />
            <h2 style={{
              margin: 0,
              fontFamily: '"Century Gothic Bold", "Noto Sans", sans-serif',
              fontSize: '1.25rem',
              color: colors.text.white,
              fontWeight: 'bold'
            }}>
              Saisir un nouveau temps
            </h2>
          </div>
          <ChevronUp size={24} color={colors.primary.teal} />
        </>
      )}
    </button>
  );
}

function FormContent({
  formData, consultants, prestataires, clientsFiltres, projetsFiltres,
  annees, factureBloquee, checkingFacture, loading, canSubmit,
  handleChange, handleSubmit, isSuperAdmin, user
}) {
  return (
    <div style={{
      padding: '1.5rem',
      animation: 'fadeInSlide 400ms ease-out forwards',
      opacity: 0,
      transform: 'translateY(-10px)'
    }}>
      <style>{`
        @keyframes fadeInSlide {
          from { opacity: 0; transform: translateY(-10px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>

      <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
        {/* Alerte facturation bloquée */}
        {factureBloquee && <BlockedAlert message={factureBloquee} />}

        {/* Indicateur de vérification */}
        {checkingFacture && <CheckingIndicator />}

        {/* Champs du formulaire */}
        <TempsFormFields
          formData={formData}
          consultants={consultants}
          prestataires={prestataires}
          clientsFiltres={clientsFiltres}
          projetsFiltres={projetsFiltres}
          annees={annees}
          factureBloquee={factureBloquee}
          handleChange={handleChange}
          isSuperAdmin={isSuperAdmin}
          user={user}
        />

        {/* Bouton submit */}
        <SubmitButton
          loading={loading}
          canSubmit={canSubmit}
          factureBloquee={factureBloquee}
        />
      </form>
    </div>
  );
}

function BlockedAlert({ message }) {
  return (
    <div style={{
      padding: '1rem',
      borderRadius: '0.5rem',
      backgroundColor: '#fef2f2',
      border: '1px solid #fecaca',
      display: 'flex',
      alignItems: 'flex-start',
      gap: '0.75rem'
    }}>
      <div style={{
        backgroundColor: '#fee2e2',
        borderRadius: '50%',
        padding: '0.5rem',
        flexShrink: 0
      }}>
        <Lock size={20} color="#dc2626" />
      </div>
      <div>
        <div style={{
          fontWeight: '600',
          color: '#991b1b',
          marginBottom: '0.25rem',
          fontSize: '0.9375rem'
        }}>
          Saisie bloquée
        </div>
        <div style={{ fontSize: '0.875rem', color: '#b91c1c', lineHeight: '1.4' }}>
          {message}
        </div>
        <div style={{
          fontSize: '0.8125rem',
          color: '#991b1b',
          marginTop: '0.5rem',
          fontStyle: 'italic'
        }}>
          Pour modifier les temps de cette période, la facture doit être remise au statut "Brouillon".
        </div>
      </div>
    </div>
  );
}

function CheckingIndicator() {
  return (
    <div style={{
      padding: '0.75rem 1rem',
      borderRadius: '0.5rem',
      backgroundColor: colors.secondary.lightTeal,
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem',
      fontSize: '0.875rem',
      color: colors.primary.teal
    }}>
      <div style={{
        width: '16px',
        height: '16px',
        border: `2px solid ${colors.secondary.lightTeal}`,
        borderTop: `2px solid ${colors.primary.teal}`,
        borderRadius: '50%',
        animation: 'spin 1s linear infinite'
      }} />
      Vérification du statut de la facture...
      <style>{`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style>
    </div>
  );
}

function SubmitButton({ loading, canSubmit, factureBloquee }) {
  return (
    <button
      type="submit"
      disabled={!canSubmit}
      style={{
        width: '100%',
        padding: '0.875rem',
        borderRadius: '0.75rem',
        border: 'none',
        backgroundColor: !canSubmit ? colors.secondary.mediumGray : colors.primary.teal,
        color: colors.text.white,
        fontSize: '1rem',
        fontWeight: '600',
        fontFamily: '"Century Gothic Bold", "Noto Sans", sans-serif',
        cursor: !canSubmit ? 'not-allowed' : 'pointer',
        transition: 'all 200ms',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '0.5rem',
        opacity: factureBloquee ? 0.6 : 1
      }}
      onMouseEnter={(e) => {
        if (canSubmit) e.target.style.backgroundColor = '#005a64';
      }}
      onMouseLeave={(e) => {
        if (canSubmit) e.target.style.backgroundColor = colors.primary.teal;
      }}
    >
      {loading ? (
        '⏳ Ajout en cours...'
      ) : factureBloquee ? (
        <>
          <Lock size={18} /> Saisie bloquée
        </>
      ) : (
        <>
          <span style={{ color: colors.primary.gold }}></span> + Ajouter le temps
        </>
      )}
    </button>
  );
}

function DuplicateModal({ duplicateInfo, onClose }) {
  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000
    }}>
      <div style={{
        backgroundColor: colors.text.white,
        borderRadius: '1rem',
        padding: '2rem',
        maxWidth: '450px',
        width: '90%',
        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
        textAlign: 'center'
      }}>
        <div style={{
          width: '60px',
          height: '60px',
          borderRadius: '50%',
          backgroundColor: '#fef3c7',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          margin: '0 auto 1.5rem'
        }}>
          <AlertTriangle size={32} color="#f59e0b" />
        </div>

        <h3 style={{
          margin: '0 0 1rem 0',
          fontFamily: '"Century Gothic Bold", "Noto Sans", sans-serif',
          fontSize: '1.25rem',
          color: colors.primary.gray
        }}>
          Entrée déjà existante
        </h3>

        <p style={{
          margin: '0 0 1.5rem 0',
          fontFamily: '"Noto Sans", sans-serif',
          fontSize: '0.9375rem',
          color: colors.primary.gray,
          lineHeight: '1.5'
        }}>
          Une entrée a déjà été renseignée pour <strong>{duplicateInfo?.consultant}</strong> sur
          le projet <strong>{duplicateInfo?.projet}</strong> pour la période
          de <strong>{duplicateInfo?.mois} {duplicateInfo?.annee}</strong>.
        </p>

        <button
          onClick={onClose}
          style={{
            padding: '0.75rem 2rem',
            borderRadius: '0.5rem',
            border: 'none',
            backgroundColor: colors.primary.teal,
            color: colors.text.white,
            fontSize: '1rem',
            fontWeight: '600',
            fontFamily: '"Noto Sans", sans-serif',
            cursor: 'pointer',
            transition: 'background-color 200ms'
          }}
          onMouseEnter={(e) => e.target.style.backgroundColor = '#005a64'}
          onMouseLeave={(e) => e.target.style.backgroundColor = colors.primary.teal}
        >
          Compris
        </button>
      </div>
    </div>
  );
}

export default TempsForm;
